import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import PageHeader from '../components/PageHeader';
import DataTable from '../components/DataTable';
import Badge from '../components/Badge';
import { couriers } from '../../services/apiClient';

const STATUS_OPTIONS = ['pending', 'rejected', 'approved', 'not_submitted', 'all'];

function toBadgeVariant(status) {
  const normalized = String(status || '').toLowerCase();
  if (normalized === 'approved') return 'success';
  if (normalized === 'rejected') return 'danger';
  if (normalized === 'pending') return 'warning';
  return 'neutral';
}

function formatVerificationStatus(status) {
  const normalized = String(status || '').toLowerCase();
  if (normalized === 'pending') return 'Em analise';
  if (normalized === 'approved') return 'Aprovado';
  if (normalized === 'rejected') return 'Reprovado';
  if (normalized === 'not_submitted') return 'Nao enviado';
  return status || '-';
}

export default function CourierVerification() {
  const [status, setStatus] = useState('pending');
  const [data, setData] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [note, setNote] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [detailLoading, setDetailLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      setLoading(true);
      setError('');
      try {
        const list = await couriers.listVerifications({ status });
        if (!mounted) return;
        setData(Array.isArray(list) ? list : []);
        if (Array.isArray(list) && list.length > 0) {
          const first = list[0];
          setSelectedId((prev) => prev || first.id);
        } else {
          setSelectedId(null);
        }
      } catch {
        if (mounted) setError('Erro ao carregar verificacoes de motoboy.');
      } finally {
        if (mounted) setLoading(false);
      }
    };
    load();
    return () => {
      mounted = false;
    };
  }, [status]);

  useEffect(() => {
    if (!selectedId) {
      setDetail(null);
      setNote('');
      return;
    }

    let mounted = true;
    const loadDetail = async () => {
      setDetailLoading(true);
      try {
        const payload = await couriers.getVerification(selectedId);
        if (!mounted) return;
        setDetail(payload || null);
        setNote(payload?.verification?.note || '');
      } catch {
        if (mounted) setDetail(null);
      } finally {
        if (mounted) setDetailLoading(false);
      }
    };
    loadDetail();
    return () => {
      mounted = false;
    };
  }, [selectedId]);

  const columns = useMemo(
    () => [
      {
        key: 'name',
        header: 'Motoboy',
        render: (row) => (
          <button
            type="button"
            className="admin-button secondary"
            style={{ padding: '6px 10px' }}
            onClick={() => setSelectedId(row.id)}
          >
            {row.name || 'Sem nome'}
          </button>
        ),
      },
      { key: 'email', header: 'Email' },
      {
        key: 'verification_status',
        header: 'Verificacao',
        render: (row) => (
          <Badge variant={toBadgeVariant(row?.verification?.status)}>
            {formatVerificationStatus(row?.verification?.status || 'not_submitted')}
          </Badge>
        ),
      },
      {
        key: 'status',
        header: 'Conta',
        render: (row) => <Badge variant={toBadgeVariant(row?.status)}>{row?.status || 'active'}</Badge>,
      },
      {
        key: 'submitted_at',
        header: 'Enviado em',
        render: (row) => row?.verification?.submitted_at
          ? new Date(row.verification.submitted_at).toLocaleString('pt-BR')
          : '-',
      },
    ],
    []
  );

  const selectedVerification = detail?.verification || null;

  const handleReview = async (decision) => {
    if (!selectedId) return;
    if (decision === 'rejected' && !String(note || '').trim()) {
      alert('Informe um motivo para reprovar.');
      return;
    }
    setSaving(true);
    try {
      await couriers.reviewVerification(selectedId, { decision, note: note || undefined });
      const [list, updated] = await Promise.all([
        couriers.listVerifications({ status }),
        couriers.getVerification(selectedId),
      ]);
      setData(Array.isArray(list) ? list : []);
      setDetail(updated || null);
      setNote(updated?.verification?.note || '');
      alert(decision === 'approved' ? 'Motoboy aprovado com sucesso.' : 'Motoboy reprovado.');
    } catch (err) {
      const detailMessage = err?.response?.data?.detail || 'Nao foi possivel salvar a avaliacao.';
      alert(detailMessage);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="admin-section">
      <PageHeader
        title="Aprovacoes de Motoboy"
        subtitle="Analise selfie, documento e dados cadastrais"
        actions={[
          <Link key="back" to="/admin/motoboys" className="admin-button secondary" style={{ textDecoration: 'none' }}>
            Voltar para Motoboys
          </Link>,
        ]}
      />

      <div className="admin-filters">
        <select className="admin-select" value={status} onChange={(event) => setStatus(event.target.value)}>
          {STATUS_OPTIONS.map((option) => (
            <option key={option} value={option}>
              {option}
            </option>
          ))}
        </select>
      </div>

      <DataTable
        columns={columns}
        data={data}
        loading={loading}
        error={error}
        emptyMessage="Nenhum cadastro encontrado para este filtro"
      />

      <div className="admin-card" style={{ display: 'grid', gap: 12 }}>
        <h3>Detalhes da verificacao</h3>
        {detailLoading ? <p>Carregando...</p> : null}
        {!detailLoading && !detail ? <p>Selecione um motoboy para revisar.</p> : null}
        {!detailLoading && detail ? (
          <>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 10 }}>
              <div>
                <strong>{detail?.name || '-'}</strong>
                <p>{detail?.email || '-'}</p>
                <p>Telefone: {detail?.phone || '-'}</p>
                <p>CPF: {detail?.cpf || '-'}</p>
                <p>Placa: {detail?.placa || '-'}</p>
                <p>Veiculo: {detail?.vehicle_type || '-'} {detail?.vehicle_model || ''}</p>
                <p>Marca da moto: {detail?.motorcycle_brand || '-'}</p>
                <p>Modelo da moto: {detail?.motorcycle_model || '-'}</p>
                <p>Contato de emergencia: {detail?.emergency_contact_phone || '-'}</p>
                <p>Data de nascimento: {detail?.birth_date || '-'}</p>
              </div>
              <div>
                <p>
                  Verificacao:{' '}
                  <Badge variant={toBadgeVariant(selectedVerification?.status)}>
                    {formatVerificationStatus(selectedVerification?.status || 'not_submitted')}
                  </Badge>
                </p>
                <p>Enviado em: {selectedVerification?.submitted_at ? new Date(selectedVerification.submitted_at).toLocaleString('pt-BR') : '-'}</p>
                <p>Revisado em: {selectedVerification?.reviewed_at ? new Date(selectedVerification.reviewed_at).toLocaleString('pt-BR') : '-'}</p>
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 10 }}>
              <div style={{ border: '1px solid var(--admin-border)', borderRadius: 12, padding: 10 }}>
                <p style={{ marginBottom: 8 }}>Selfie</p>
                {selectedVerification?.face_image_url ? (
                  <a href={selectedVerification.face_image_url} target="_blank" rel="noreferrer">
                    <img
                      src={selectedVerification.face_image_url}
                      alt="Selfie"
                      style={{ width: '100%', maxHeight: 280, objectFit: 'cover', borderRadius: 8 }}
                    />
                  </a>
                ) : selectedVerification?.has_face_image ? (
                  <p>Selfie enviada. Atualize a pagina para gerar nova URL de visualizacao.</p>
                ) : (
                  <p>Sem selfie enviada.</p>
                )}
              </div>

              <div style={{ border: '1px solid var(--admin-border)', borderRadius: 12, padding: 10 }}>
                <p style={{ marginBottom: 8 }}>Documento com foto</p>
                {selectedVerification?.document_image_url ? (
                  <a href={selectedVerification.document_image_url} target="_blank" rel="noreferrer">
                    <img
                      src={selectedVerification.document_image_url}
                      alt="Documento"
                      style={{ width: '100%', maxHeight: 280, objectFit: 'cover', borderRadius: 8 }}
                    />
                  </a>
                ) : selectedVerification?.has_document_image ? (
                  <p>Documento enviado. Atualize a pagina para gerar nova URL de visualizacao.</p>
                ) : (
                  <p>Sem documento enviado.</p>
                )}
              </div>
            </div>

            <div style={{ display: 'grid', gap: 8 }}>
              <label htmlFor="verification-note">Observacao do admin</label>
              <textarea
                id="verification-note"
                className="admin-input"
                rows={3}
                value={note}
                onChange={(event) => setNote(event.target.value)}
                placeholder="Ex.: Documento ilegivel, rosto nao corresponde, etc."
              />
            </div>

            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <button type="button" className="admin-button" disabled={saving} onClick={() => handleReview('approved')}>
                {saving ? 'Salvando...' : 'Aprovar motoboy'}
              </button>
              <button type="button" className="admin-button secondary" disabled={saving} onClick={() => handleReview('rejected')}>
                {saving ? 'Salvando...' : 'Reprovar motoboy'}
              </button>
            </div>
          </>
        ) : null}
      </div>
    </div>
  );
}
